import { BRIEF_DRAFTING_QUERY_KEY } from "@entities/brief/lib/constants";
import { useMutation } from "@shared/lib/hooks";
import { ToastService } from "@shared/lib/services";
import { BackednResponse } from "@shared/lib/types";
import {
  IDraftingComments,
  IDraftingResponse,
  IResolveDraftingComment,
} from "@shared/lib/types/drafting";
import { useQueryClient } from "@tanstack/react-query";
import { BriefService } from "../../services/brief.service";

export function useResolveDraftingComment(draftingId: string) {
  const queryClient = useQueryClient();

  const updateCommentResolve = (
    payload: IResolveDraftingComment,
    data?: BackednResponse<{ comment: IDraftingComments }>
  ) => {
    queryClient.setQueryData(
      BRIEF_DRAFTING_QUERY_KEY(draftingId),
      (oldData: BackednResponse<IDraftingResponse>) => {
        return {
          ...oldData,
          data: {
            ...oldData.data,
            drafting: {
              ...oldData.data.drafting,
              draftingContents: oldData.data?.drafting?.draftingContents?.map(
                (content) => {
                  if (content._id === payload.draftingVersionId) {
                    return {
                      ...content,
                      comments: (content.comments || []).map((comment) => {
                        if (comment._id === payload.commentId) {
                          return {
                            ...comment,
                            isResolved:
                              data?.data?.comment?.isResolved ??
                              payload.isResolved,
                          };
                        }
                        return comment;
                      }),
                    };
                  }
                  return content;
                }
              ),
            },
          },
        };
      }
    );
  };

  const {
    mutate: resolveComment,
    isLoading: isResolving,
    isSuccess: isResolved,
    variables,
  } = useMutation({
    mutationFn: async (payload: IResolveDraftingComment) => {
      // Optimistically update
      updateCommentResolve(payload);
      return BriefService.resolveDraftingComment(payload).catch(async (err) => {
        const responseBody = await err.response?.json?.();
        if (responseBody?.msg) ToastService.error(responseBody.msg);
        // Revert on error
        updateCommentResolve({
          ...payload,
          isResolved: !payload.isResolved,
        });
        throw err;
      });
    },
    options: {
      skipInvalidation: true,
    },
    onSuccess: (c, data, params) => {
      updateCommentResolve(params, data);
    },
  });

  return {
    resolveComment,
    isResolving,
    isResolved,
    resolveCommentId: variables?.commentId || "",
  };
}
